/**
 * LimitReachedModal - Modal genérico para exibir quando um limite de recurso foi atingido
 * 
 * Funcionalidades:
 * - Exibe mensagem sobre o limite atingido
 * - Oferece opções: contratar add-on ou fazer upgrade de plano
 * - Design consistente com o sistema
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  Package, 
  ArrowUpCircle,
  Users,
  Inbox,
  MessageSquare,
  Megaphone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Tipos de recursos que podem ter limite
type ResourceType = 'agent' | 'inbox' | 'channel' | 'campaign' | 'message';

interface LimitReachedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType: ResourceType;
  currentCount?: number;
  maxAllowed?: number;
  customMessage?: string;
  showAddonOption?: boolean;
  showUpgradeOption?: boolean;
}

// Configuração de ícones e labels por tipo de recurso
const resourceConfig: Record<ResourceType, { 
  icon: typeof Users; 
  label: string; 
  title: string;
  description: string;
}> = {
  agent: {
    icon: Users,
    label: 'Agentes',
    title: 'Limite de Agentes Atingido',
    description: 'Você atingiu o limite máximo de agentes para o seu plano atual.',
  },
  inbox: {
    icon: Inbox,
    label: 'Inboxes',
    title: 'Limite de Inboxes Atingido',
    description: 'Você atingiu o limite máximo de caixas de entrada para o seu plano atual.',
  },
  channel: {
    icon: MessageSquare,
    label: 'Canais',
    title: 'Limite de Canais Atingido',
    description: 'Você atingiu o limite máximo de canais para o seu plano atual.',
  },
  campaign: {
    icon: Megaphone,
    label: 'Campanhas',
    title: 'Módulo de Campanhas Não Disponível',
    description: 'O módulo de campanhas não está incluído no seu plano atual.',
  },
  message: {
    icon: MessageSquare,
    label: 'Mensagens',
    title: 'Limite de Mensagens Atingido',
    description: 'Você atingiu o limite mensal de mensagens do seu plano.',
  },
};

export function LimitReachedModal({
  open,
  onOpenChange,
  resourceType,
  currentCount,
  maxAllowed,
  customMessage,
  showAddonOption = true,
  showUpgradeOption = true,
}: LimitReachedModalProps) {
  const navigate = useNavigate();
  const config = resourceConfig[resourceType];
  const IconComponent = config.icon;

  // Handler para ir à página de planos
  const handleGoToPlans = () => {
    onOpenChange(false);
    navigate('/tenant/plans');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <DialogTitle>{config.title}</DialogTitle>
              <DialogDescription className="mt-1">
                {customMessage || config.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Exibir contagem atual vs limite */}
        {currentCount !== undefined && maxAllowed !== undefined && (
          <div className="my-4 rounded-lg border bg-muted/50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IconComponent className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">{config.label}</span>
              </div>
              <span className="text-lg font-bold text-destructive">
                {currentCount} / {maxAllowed}
              </span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-muted">
              <div 
                className="h-full rounded-full bg-destructive transition-all"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          
          {showAddonOption && resourceType !== 'campaign' && (
            <Button variant="secondary" onClick={handleGoToPlans}>
              <Package className="mr-2 h-4 w-4" />
              Contratar Add-on
            </Button>
          )}
          
          {showUpgradeOption && (
            <Button onClick={handleGoToPlans}>
              <ArrowUpCircle className="mr-2 h-4 w-4" />
              Fazer Upgrade
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
